import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SalesMarketingPage.css';

const SalesMarketingPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: 'Lead Management',
      icon: '🎯',
      description: 'Streamline your sales pipeline with intelligent lead tracking and nurturing systems.',
      features: [
        'Lead capture and qualification',
        'Automated lead scoring',
        'Pipeline management dashboard',
        'Lead nurturing campaigns',
        'Conversion tracking and analytics',
        'Follow-up automation'
      ]
    },
    {
      title: 'Digital Marketing',
      icon: '📱',
      description: 'Comprehensive digital marketing strategies that drive engagement and conversions.',
      features: [
        'Social media marketing',
        'Content marketing strategy',
        'Email marketing campaigns',
        'Search engine optimization (SEO)',
        'Pay-per-click (PPC) advertising',
        'Marketing automation'
      ]
    },
    {
      title: 'CRM Solutions',
      icon: '💼',
      description: 'Custom CRM implementations to manage customer relationships effectively.',
      features: [
        'CRM platform setup and customization',
        'Contact and account management',
        'Sales automation workflows',
        'Customer journey mapping',
        'Integration with existing tools',
        'Training and support'
      ]
    },
    {
      title: 'Sales Analytics',
      icon: '📊',
      description: 'Data-driven insights to optimize your sales performance and strategies.',
      features: [
        'Sales performance dashboards',
        'Revenue forecasting',
        'KPI tracking and reporting',
        'Customer behavior analysis',
        'ROI measurement',
        'Predictive analytics'
      ]
    }
  ];

  const platforms = [
    { name: 'HubSpot', category: 'CRM' },
    { name: 'Salesforce', category: 'CRM' },
    { name: 'Zoho CRM', category: 'CRM' },
    { name: 'Google Analytics', category: 'Analytics' },
    { name: 'Meta Ads', category: 'Advertising' },
    { name: 'Google Ads', category: 'Advertising' },
    { name: 'Mailchimp', category: 'Email Marketing' },
    { name: 'LinkedIn Sales', category: 'Social Selling' },
    { name: 'Tableau', category: 'Analytics' },
    { name: 'Hootsuite', category: 'Social Media' }
  ];

  const benefits = [
    {
      title: 'Increased Revenue',
      description: 'Boost sales with optimized processes and strategies',
      icon: '💰'
    },
    {
      title: 'Better ROI',
      description: 'Maximize marketing spend with data-driven decisions',
      icon: '📈'
    },
    {
      title: 'Customer Insights',
      description: 'Understand your audience and their needs better',
      icon: '🔍'
    },
    {
      title: 'Scalable Growth',
      description: 'Build systems that grow with your business',
      icon: '🚀'
    }
  ];

  const strategies = [
    {
      name: 'Inbound Marketing',
      description: 'Attract customers through valuable content and experiences',
      icon: '🧲',
      tactics: ['Content creation', 'SEO optimization', 'Lead magnets', 'Blog strategy']
    },
    {
      name: 'Outbound Sales',
      description: 'Proactive outreach to prospects and decision-makers',
      icon: '📞',
      tactics: ['Cold outreach', 'Email campaigns', 'Sales calls', 'LinkedIn prospecting']
    },
    {
      name: 'Account-Based Marketing',
      description: 'Targeted campaigns for high-value accounts',
      icon: '🎪',
      tactics: ['Account research', 'Personalized content', 'Multi-channel engagement', 'Executive alignment']
    },
    {
      name: 'Social Selling',
      description: 'Build relationships and trust through social platforms',
      icon: '🤝',
      tactics: ['LinkedIn presence', 'Content sharing', 'Engagement strategy', 'Network building']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Audit',
      description: 'Analyze your current sales and marketing processes, identify gaps and opportunities'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Create a customized roadmap aligned with your business goals and target audience'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Execute campaigns, set up tools, and build workflows for optimal performance'
    },
    {
      step: '04',
      title: 'Optimization',
      description: 'Monitor results, A/B test, and continuously improve for better outcomes'
    },
    {
      step: '05',
      title: 'Reporting & Growth',
      description: 'Regular performance reviews and strategic adjustments to scale results'
    }
  ];

  const metrics = [
    { label: 'Lead Generation', impact: '250% increase' },
    { label: 'Conversion Rate', impact: '180% improvement' },
    { label: 'Customer Acquisition Cost', impact: '45% reduction' },
    { label: 'Sales Cycle Time', impact: '30% faster' },
    { label: 'Customer Retention', impact: '95% rate' },
    { label: 'Marketing ROI', impact: '4.5x average' }
  ];

  return (
    <div className="sales-marketing-page">
      {/* Hero Section */}
      <section className="sales-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <div className="hero-content">
            <h1 className="hero-title">
              Sales & Marketing Solutions
            </h1>
            <p className="hero-subtitle">
              Data-driven strategies and cutting-edge tools to accelerate your growth and dominate your market
            </p>
            <div className="hero-stats">
              <div className="stat">
                <h3>300+</h3>
                <p>Campaigns Launched</p>
              </div>
              <div className="stat">
                <h3>$5M+</h3>
                <p>Revenue Generated</p>
              </div>
              <div className="stat">
                <h3>4.5x</h3>
                <p>Average ROI</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-detail">
        <div className="container">
          <h2 className="section-title">Our Marketing Services</h2>
          <p className="section-subtitle">
            Comprehensive solutions to drive growth and maximize revenue
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-detail-card">
                <div className="service-icon-large">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="service-desc">{service.description}</p>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="platforms">
        <div className="container">
          <h2 className="section-title">Tools & Platforms We Master</h2>
          <p className="section-subtitle">
            Expert implementation and optimization across leading platforms
          </p>
          <div className="platforms-grid">
            {platforms.map((platform, index) => (
              <div key={index} className="platform-item">
                <span className="platform-name">{platform.name}</span>
                <span className="platform-category">{platform.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategies */}
      <section className="strategies">
        <div className="container">
          <h2 className="section-title">Our Marketing Strategies</h2>
          <p className="section-subtitle">
            Proven approaches to reach and convert your target audience
          </p>
          <div className="strategies-grid">
            {strategies.map((strategy, index) => (
              <div key={index} className="strategy-card">
                <div className="strategy-icon">{strategy.icon}</div>
                <h3>{strategy.name}</h3>
                <p className="strategy-desc">{strategy.description}</p>
                <div className="tactics-list">
                  {strategy.tactics.map((tactic, idx) => (
                    <span key={idx} className="tactic-badge">{tactic}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="container">
          <h2 className="section-title">What You'll Achieve</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <h2 className="section-title">Our Approach</h2>
          <p className="section-subtitle">
            A proven methodology for marketing success
          </p>
          <div className="process-timeline">
            {process.map((item, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="metrics">
        <div className="container">
          <h2 className="section-title">Results That Matter</h2>
          <p className="section-subtitle">
            Average performance improvements across our client base
          </p>
          <div className="metrics-grid">
            {metrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-impact">{metric.impact}</div>
                <div className="metric-label">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Accelerate Your Growth?</h2>
            <p>Let's create a winning sales and marketing strategy for your business</p>
            <div className="cta-buttons">
              <button className="primary-btn" onClick={() => navigate('/#contact')}>
                Get a Free Consultation
              </button>
              <button className="secondary-btn" onClick={() => navigate('/')}>
                Explore More Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesMarketingPage;
